<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { Jodit } from 'jodit'
import 'jodit/es2021/jodit.min.css'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  height: {
    type: String,
    default: '600px',
  },
})

const emit = defineEmits(['update:modelValue', 'ready'])

const editorContainer = ref(null)
let editor = null
let isUpdatingFromProp = false

// LiquidJS tags (`{{ }}`, `{% %}`) use `{` / `}` which the browser does not
// re-encode, so no escaping is required. Any legacy underscore tags that may
// still be present are handled as a safety net during the migration.
const encodeTemplateTagsForEditor = html => {
  if (!html) return html

  return html.replace(/<%/g, '&lt;%')
}

const decodeTemplateTagsFromEditor = html => {
  if (!html) return html

  return html.replace(/&lt;%/g, '<%')
}

// RM-165/RM-227: discard pasted font-family / font-size / color / letter-spacing /
// line-height (and Word/Outlook residue) so content inherits the standard email
// typography, which the send path sets once on the parent container. Mirrors
// lib/emailStyleTokens.normalizeEmailHtml (kept inline here to avoid pulling a
// server module into the client bundle; the server re-applies it on save).
const TYPOGRAPHY_PROPS = ['font-family', 'font-size', 'font', 'color', 'letter-spacing', 'line-height', 'text-indent']
const hasPaintedBackground = v => /(^|;)\s*background(-color)?\s*:(?!\s*(transparent|none|initial|inherit|white|#fff|#ffffff|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))\s*(;|$))/i.test(v)
const stripTypography = html => {
  if (!html) return html
  const stripDecls = value => {
    if (/\{\{|\{%/.test(value)) return value // Liquid-driven style — keep
    const keepColor = hasPaintedBackground(value) // a button keeps its text color

    return value
      .split(';')
      .map(d => d.trim())
      .filter(Boolean)
      .filter(d => {
        const prop = d.slice(0, d.indexOf(':')).trim().toLowerCase()
        if (prop.startsWith('mso-')) return false
        if (prop === 'color' && keepColor) return true

        return !TYPOGRAPHY_PROPS.includes(prop)
      })
      .join(';')
  }

  const cleanAttr = (m, v) => {
    const cleaned = stripDecls(v)

    return cleaned ? ` style="${cleaned}"` : ''
  }

  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<\/?o:p\b[^>]*>/gi, '')
    .replace(/<\/?font\b[^>]*>/gi, '')
    .replace(/\s*style\s*=\s*"([^"]*)"/gi, cleanAttr)
    .replace(/\s*style\s*=\s*'([^']*)'/gi, cleanAttr)
}

onMounted(async () => {
  await nextTick()

  const heightPx = parseInt(props.height) || 600

  editor = Jodit.make(editorContainer.value, {
    height: heightPx,
    minHeight: heightPx,
    toolbarAdaptive: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,

    // RM-165: paste is processed so pasted font/size/color are discarded (see
    // the `processPaste` handler below). Content keeps bold/italic/underline,
    // links, and lists — which inherit the standard typography — but never
    // foreign font-family/size/color.
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
    processPasteHTML: true,

    cleanHTML: {
      replaceNBSP: false,
      allowTags: false,
      denyTags: { font: true },
      cleanOnPaste: false,
    },

    // RM-227: Enter creates a new <div> line — the same structure the sent
    // emails use (one line = one div, see lib/emailMailSafe.js) and the same
    // thing Apple Mail produces when a recipient edits the email by hand. The
    // server adds the mail-safe line style on save. (<br> line breaks inside a
    // shared block were what made hand-edits insert blank gaps.)
    enter: 'div',

    // RM-165: only bold/italic/underline (+ links, lists) are offered — no
    // font-family, font-size, or text-color controls, and no raw-HTML `source`
    // escape hatch, so admin edits always render in the standard typography.
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'paragraph',
      'align',
      '|',
      'ul',
      'ol',
      '|',
      'link',
      'image',
      '|',
      'table',
      '|',
      'indent',
      'outdent',
      '|',
      'hr',
      '|',
      'undo',
      'redo',
    ],

    // Don't use iframe - simpler and less prone to style leakage
    iframe: false,
  })

  // Load initial content
  if (props.modelValue) {
    editor.value = encodeTemplateTagsForEditor(props.modelValue)
  }

  // RM-165: strip font-family/size/color from anything pasted in, so pasted
  // content (e.g. from Word/Docs) drops to the standard typography.
  editor.events.on('processPaste', (event, html) => stripTypography(html))

  // Emit changes
  editor.events.on('change', () => {
    if (!isUpdatingFromProp) {
      emit('update:modelValue', decodeTemplateTagsFromEditor(editor.value))
    }
  })

  emit('ready', editor)
})

// Sync external content changes into editor
watch(() => props.modelValue, newValue => {
  if (!editor) return

  const decoded = decodeTemplateTagsFromEditor(editor.value)

  if (newValue !== decoded) {
    isUpdatingFromProp = true
    editor.value = encodeTemplateTagsForEditor(newValue)
    nextTick(() => {
      isUpdatingFromProp = false
    })
  }
})

onBeforeUnmount(() => {
  if (editor) {
    editor.destruct()
    editor = null
  }
})

// Exposed methods (same interface as the previous GrapesEditor)
const getHtml = () => {
  if (!editor) return ''

  return decodeTemplateTagsFromEditor(editor.value)
}

const setContent = html => {
  if (editor) {
    isUpdatingFromProp = true
    editor.value = encodeTemplateTagsForEditor(html)
    nextTick(() => {
      isUpdatingFromProp = false
    })
  }
}

const insertHtml = html => {
  if (editor) {
    editor.s.insertHTML(html)
  }
}

defineExpose({
  getHtml,
  setContent,
  insertHtml,
  getEditor: () => editor,
})
</script>

<template>
  <div class="jodit-editor-wrapper">
    <div ref="editorContainer" />
  </div>
</template>

<style lang="scss">
.jodit-editor-wrapper {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;

  // Normalize Jodit toolbar font size to avoid iOS zoom issues
  .jodit-toolbar__box {
    .jodit-toolbar-button__button {
      font-size: 16px;
    }
  }

  // Source editor textarea
  .jodit-source__mirror {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 13px !important;
    line-height: 1.5;
  }
}
</style>
